import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BadgeCheck, CheckCircle2, FileText, TrendingDown, TrendingUp, Sigma, Users, Award, Paperclip, Download, XCircle, Info, ChevronDown, ChevronUp, Star } from "lucide-react";

const fmt = (n) => (n == null ? "-" : n.toLocaleString("tr-TR", { maximumFractionDigits: 2 }));
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("tr-TR") : "-");

const previewKind = (f) => {
  const ct = f.content_type || "";
  if (ct === "application/pdf") return "pdf";
  if (ct.startsWith("image/")) return "image";
  if (ct.startsWith("text/")) return "text";
  if (["officedocument", "ms-excel", "msword", "ms-powerpoint"].some((x) => ct.includes(x)) || /\.(pptx?|docx?|xlsx?)$/i.test(f.original_filename || "")) return "office";
  return "none";
};

const FileChip = ({ file, onOpen }) => (
  <button onClick={() => onOpen(file)} data-testid={`approval-file-${file.id}`} className="inline-flex items-center gap-1.5 border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-100 transition-colors duration-200">
    <Paperclip size={12} className="text-slate-400" />
    <span className="max-w-[220px] truncate">{file.original_filename}</span>
  </button>
);

export default function ApprovalPublicPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [note, setNote] = useState("");
  const [approving, setApproving] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [filePreview, setFilePreview] = useState(null);

  const openFile = async (file) => {
    try {
      const { data: d } = await axios.get(`${API}/public/approval/${token}/file-url/${file.id}`);
      const kind = previewKind(file);
      if (kind === "none") {
        window.open(d.url, "_blank");
        return;
      }
      setFilePreview({ file, kind, rawUrl: d.url, url: kind === "office" ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(d.url)}` : d.url });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const load = () =>
    axios.get(`${API}/public/approval/${token}`).then((r) => setData(r.data)).catch((e) => setError(apiError(e)));

  useEffect(() => { load(); }, [token]);

  const approve = async () => {
    setApproving(true);
    try {
      await axios.post(`${API}/public/approval/${token}/approve`, { offer_id: confirm.id, note });
      toast.success("Teklif onaylandı, yöneticiler bilgilendirildi");
      setConfirm(null);
      setNote("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setApproving(false);
    }
  };

  if (error)
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center p-6">
        <div className="border border-slate-200 bg-white p-10 text-center max-w-md" data-testid="approval-error">
          <p className="font-heading text-xl font-bold">Bağlantı geçersiz</p>
          <p className="text-sm text-slate-500 mt-2">{error}</p>
        </div>
      </div>
    );
  if (!data)
    return <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center text-sm text-slate-500">Yükleniyor...</div>;

  const { approval, request, project, offers, analysis } = data;
  const approved = approval.status === "approved";
  const summary = approval.approvers_summary || [];
  const myApproved = !approved && approval.my_status === "approved";
  const selectedOfferId = !approved ? approval.selected_offer_id : null;
  const allFiles = [...(data.request_files || []), ...(data.project_files || [])];
  const diffPct = analysis.min_try && analysis.max_try ? Math.round(((analysis.max_try - analysis.min_try) / analysis.min_try) * 100) : null;

  return (
    <div className="min-h-screen bg-[#F5F7FA] text-slate-900 pb-20" data-testid="approval-public-page">
      <header className="bg-white border-b border-slate-200 px-6 py-5">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <span className="font-heading text-xl font-extrabold tracking-tight">Gett<span className="text-slate-400">Proposal</span></span>
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mt-0.5">Teklif Onayı</p>
          </div>
          {approved ? (
            <Badge variant="outline" className="rounded-none bg-emerald-500/10 text-emerald-600 border-emerald-500/20 gap-1.5 px-3 py-1.5"><CheckCircle2 size={13} /> Onaylandı</Badge>
          ) : myApproved ? (
            <Badge variant="outline" className="rounded-none bg-amber-500/10 text-amber-600 border-amber-500/20 gap-1.5 px-3 py-1.5"><CheckCircle2 size={13} /> Onayınız alındı — diğer onaylar bekleniyor</Badge>
          ) : (
            <Badge variant="outline" className="rounded-none bg-amber-500/10 text-amber-600 border-amber-500/20 px-3 py-1.5">Onayınız bekleniyor</Badge>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 mt-10 space-y-10">
        <section>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">İhale</p>
          <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1" data-testid="approval-request-title">{request.title}</h1>
          <p className="text-sm text-slate-500 mt-2">
            {project?.zone && <>{project.zone} · Stand {project.stand_no} · {project.project_type}{project.area_m2 ? ` · ${project.area_m2} m²` : ""} · </>}
            Son tarih: {fmtDate(request.deadline)}{request.deadline_time ? ` ${request.deadline_time}` : ""}
          </p>
          {approval.note && <p className="mt-3 text-sm bg-white border border-slate-200 px-4 py-3 max-w-2xl">{approval.note}</p>}
          {summary.length > 1 && (
            <div className="mt-4 max-w-2xl space-y-2" data-testid="approval-approvers-progress">
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2">
                Bu onay için {summary.length} onaylayıcı belirlenmiştir. Teklif ancak tüm onaylayıcılar onayladığında kabul edilir.
              </p>
              <div className="flex flex-wrap gap-2">
                {summary.map((s) => (
                  <span key={s.name} className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border ${s.status === "approved" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
                    {s.status === "approved" ? <CheckCircle2 size={12} /> : null}
                    <strong>{s.name}</strong> — {s.status === "approved" ? "Onayladı" : "Bekleniyor"}
                  </span>
                ))}
              </div>
            </div>
          )}
          {myApproved && (
            <div className="mt-4 border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 max-w-2xl" data-testid="approval-partial-banner">
              <CheckCircle2 size={14} className="inline mr-1.5 -mt-0.5" />
              Onayınız kaydedildi. Teklif, diğer onaylayıcıların da onayı sonrasında kabul edilecektir.
            </div>
          )}
          {approved && (
            <div className="mt-4 border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 text-sm text-emerald-700 max-w-2xl" data-testid="approval-done-banner">
              <CheckCircle2 size={14} className="inline mr-1.5 -mt-0.5" />
              Onayınız alınmıştır ({fmtDate(approval.approved_at)}). Yönetim ekibi bilgilendirildi.
            </div>
          )}
        </section>

        <section className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="approval-analysis">
          {[
            { icon: Users, label: "Davet Edilen", value: analysis.sent_suppliers },
            { icon: FileText, label: "Gelen Teklif", value: analysis.total_offers },
            { icon: Sigma, label: "Ortalama (TL)", value: fmt(analysis.avg_try) },
            { icon: TrendingDown, label: "En Düşük (TL)", value: fmt(analysis.min_try) },
            { icon: TrendingUp, label: "En Yüksek (TL)", value: fmt(analysis.max_try) },
          ].map((c) => (
            <div key={c.label} className="border border-slate-200 bg-white p-4">
              <c.icon size={15} className="text-slate-400" />
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-2">{c.label}</div>
              <div className="font-heading text-lg font-bold tracking-tight mt-0.5">{c.value}</div>
            </div>
          ))}
        </section>
        <p className="text-xs text-slate-400 -mt-6">
          {diffPct != null && <>En düşük ile en yüksek teklif arasında %{diffPct} fark var. </>}
          {analysis.rates?.USD && <>Kur: 1 USD ≈ {fmt(analysis.rates.USD)} TL · 1 EUR ≈ {fmt(analysis.rates.EUR)} TL. </>}
          Size {analysis.shown_count === analysis.total_offers ? "tüm teklifler" : `en düşük fiyatlı ${analysis.shown_count} teklif`} sunulmuştur.
        </p>

        {(data.all_offers || []).length > 0 && (
          <section className="-mt-2" data-testid="all-offers-section">
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              data-testid="toggle-all-offers"
              className="flex items-center gap-2 text-sm font-semibold text-slate-700 border border-slate-300 bg-white hover:bg-slate-100 px-4 py-2 transition-colors duration-150"
            >
              {showAll ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              {showAll ? "İhaledeki Tüm Teklifleri Gizle" : `İhaledeki Tüm Teklifleri Göster (${data.all_offers.length})`}
            </button>
            {showAll && (
              <div className="border border-slate-200 bg-white divide-y divide-slate-100 mt-2" data-testid="all-offers-list">
                {data.all_offers.map((o, i) => (
                  <div key={i} className="px-4 py-3 flex items-center gap-3 text-sm flex-wrap" data-testid={`all-offer-row-${i}`}>
                    <span className="w-6 h-6 bg-slate-900 text-white text-xs font-bold flex items-center justify-center shrink-0">{o.rank}</span>
                    <span className="font-semibold min-w-0 flex-1">{o.company_name}</span>
                    {o.status === "accepted" && <span className="text-[10px] font-bold px-2 py-0.5 bg-emerald-100 text-emerald-800 uppercase">Kazanan</span>}
                    {o.status === "rejected" && <span className="text-[10px] font-bold px-2 py-0.5 bg-red-50 text-red-500 uppercase">Reddedildi</span>}
                    <span className="text-right">
                      <span className="font-heading font-bold">{fmt(o.price)} {o.currency}</span>
                      {o.currency !== "TRY" && <span className="text-xs text-slate-400 block">≈ {fmt(o.price_try)} TL</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {allFiles.length > 0 ? (
          <section data-testid="approval-files">
            <h2 className="text-base md:text-lg font-semibold tracking-tight">Tasarım ve Proje Dosyaları</h2>
            <p className="text-xs text-slate-400 mt-1">Dosyalara tıklayarak tasarımları sayfadan ayrılmadan görüntüleyebilirsiniz.</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {allFiles.map((f) => <FileChip key={f.id} file={f} onOpen={openFile} />)}
            </div>
          </section>
        ) : (
          <section data-testid="approval-files-empty">
            <h2 className="text-base md:text-lg font-semibold tracking-tight">Tasarım ve Proje Dosyaları</h2>
            <p className="text-xs text-slate-400 mt-1">Bu ihale için yüklenmiş tasarım dosyası bulunmuyor.</p>
          </section>
        )}

        <section data-testid="approval-offers">
          <h2 className="text-base md:text-lg font-semibold tracking-tight">Teklifler</h2>
          <p className="text-xs text-slate-400 mt-1" data-testid="approval-scope-note">
            {approval.scope === "lowest" ? "Bu onaya yalnızca en düşük fiyatlı teklif dahil edilmiştir."
              : approval.scope === "top3" || approval.scope === "top5" ? `Bu onaya en düşük fiyatlı ${offers.length} teklif dahil edilmiştir.`
              : approval.scope === "custom" ? `Bu onaya komisyon tarafından özel olarak seçilen ${offers.length} teklif dahil edilmiştir.`
              : `Bu onaya ihaleye gelen tüm teklifler (${offers.length}) dahil edilmiştir.`}
            {offers.some((o) => o.recommended) && " ★ Komisyon tavsiyesi işaretlidir; nihai karar size aittir."}
          </p>
          {!approved && !myApproved && (
            <div className="border border-amber-300 bg-amber-50/70 p-4 text-sm text-amber-900 flex gap-2.5 mt-3" data-testid="approval-info-banner">
              <Info size={16} className="shrink-0 mt-0.5 text-amber-600" />
              <span>
                {summary.length > 1 ? (
                  <>Onayınız kaydedilir; ihale ancak <strong>tüm onaylayıcılar aynı teklifi onayladığında</strong> otomatik olarak sonuçlanır: onaylanan firma <strong>kazanan</strong> ilan edilir, diğer teklifler <strong>reddedilir</strong> ve firmalara bilgilendirme e-postaları gönderilir.</>
                ) : (
                  <>Bir teklifi onayladığınızda ihale <strong>otomatik olarak sonuçlanır</strong>: onayladığınız firma <strong>kazanan</strong> ilan edilir,
                  diğer tüm teklifler <strong>reddedilir</strong> ve ilgili firmalara bilgilendirme e-postaları gönderilir. Bu işlem geri alınamaz.</>
                )}
              </span>
            </div>
          )}
          {selectedOfferId && !myApproved && (
            <div className="border border-blue-300 bg-blue-50 p-4 text-sm text-blue-900 flex gap-2.5 mt-3" data-testid="approval-selected-banner">
              <Info size={16} className="shrink-0 mt-0.5 text-blue-600" />
              <span>Diğer onaylayıcı(lar) aşağıda <strong>"Seçilen Teklif"</strong> olarak işaretli teklifi onayladı. Onay sürecinin tamamlanması için aynı teklifi onaylamanız gerekir.</span>
            </div>
          )}
          <div className="mt-3 space-y-3">
            {offers.map((o) => {
              const isApproved = approval.approved_offer_id === o.id || (myApproved && approval.my_offer_id === o.id);
              const isSelected = selectedOfferId === o.id;
              return (
                <div key={o.id} data-testid={`approval-offer-${o.id}`}
                     className={`border bg-white p-5 flex flex-wrap items-center gap-4 ${isApproved || isSelected ? "border-emerald-500/50 ring-1 ring-emerald-500/30" : "border-slate-200"}`}>
                  <div className={`shrink-0 w-12 h-12 flex items-center justify-center font-heading font-extrabold text-lg ${o.rank === 1 ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}>
                    {o.rank}.
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold flex items-center gap-2 flex-wrap">
                      {o.company_name}
                      {o.rank === 1 && <Badge variant="outline" className="rounded-none text-[10px] bg-emerald-500/10 text-emerald-600 border-emerald-500/20 gap-1"><Award size={10} /> En Düşük</Badge>}
                      {o.recommended && <Badge variant="outline" data-testid={`recommended-badge-${o.id}`} className="rounded-none text-[10px] bg-amber-500 text-white border-amber-500 gap-1"><Star size={10} /> Komisyon Tavsiyesi</Badge>}
                      {isApproved && <Badge variant="outline" className="rounded-none text-[10px] bg-emerald-600 text-white border-emerald-600 gap-1"><BadgeCheck size={10} /> Onayladınız</Badge>}
                      {isSelected && !isApproved && <Badge variant="outline" data-testid={`selected-badge-${o.id}`} className="rounded-none text-[10px] bg-blue-600 text-white border-blue-600 gap-1"><BadgeCheck size={10} /> Seçilen Teklif</Badge>}
                      {approved && !isApproved && <Badge variant="outline" data-testid={`lost-badge-${o.id}`} className="rounded-none text-[10px] bg-red-500/10 text-red-500 border-red-500/20 gap-1"><XCircle size={10} /> Kaybetti</Badge>}
                    </div>
                    {o.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{o.description}</p>}
                    {o.files?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {o.files.map((f) => <FileChip key={f.id} file={f} onOpen={openFile} />)}
                      </div>
                    )}
                    {o.negotiation?.length > 0 && (
                      <div className="mt-3 border-l-2 border-amber-300 pl-3 space-y-1" data-testid={`negotiation-history-${o.id}`}>
                        <div className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold">Pazarlık Geçmişi</div>
                        {o.negotiation.map((n, i) => (
                          <div key={i} className="text-xs text-slate-600 flex items-center gap-1.5 flex-wrap">
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${n.kind === "accepted" || n.kind === "applied" || n.kind === "current" || n.kind === "revision_down" ? "bg-emerald-500" : n.kind === "rejected" || n.kind === "revision_up" ? "bg-red-400" : n.kind === "counter" ? "bg-blue-500" : n.kind === "first" ? "bg-slate-900" : n.kind === "revision" ? "bg-slate-400" : "bg-amber-500"}`} />
                            <span>{n.label}{n.price != null && <>: <strong className="text-slate-900">{fmt(n.price)} {n.currency}</strong></>}</span>
                            {n.at && <span className="text-slate-400">· {new Date(n.at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-heading text-2xl font-extrabold tracking-tight">{fmt(o.price)} <span className="text-sm font-medium text-slate-500">{o.currency}</span></div>
                    {o.currency !== "TRY" && <div className="text-xs text-slate-400">≈ {fmt(o.price_try)} TL</div>}
                  </div>
                  {!approved && !myApproved && (
                    selectedOfferId && selectedOfferId !== o.id ? (
                      <span className="text-xs text-slate-400 border border-slate-200 px-3 py-2 shrink-0" data-testid={`approve-disabled-${o.id}`}>Yalnızca seçilen teklif onaylanabilir</span>
                    ) : (
                      <Button onClick={() => setConfirm(o)} data-testid={`approve-offer-button-${o.id}`}
                              className="rounded-none h-10 px-5 bg-emerald-600 hover:bg-emerald-500 text-white gap-2 font-semibold transition-colors duration-200 shrink-0">
                        <CheckCircle2 size={14} /> Bu Teklifi Onayla
                      </Button>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </main>

      <Dialog open={!!filePreview} onOpenChange={(o) => { if (!o) setFilePreview(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-5xl w-[92vw] h-[88vh] flex flex-col p-0 gap-0" data-testid="approval-file-preview-modal">
          {filePreview && (
            <>
              <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 pr-12 shrink-0">
                <DialogTitle className="text-sm font-medium truncate">{filePreview.file.original_filename}</DialogTitle>
                <Button size="sm" className="rounded-none h-8 bg-slate-900 text-white hover:bg-slate-700 gap-1.5 font-semibold transition-colors duration-200 shrink-0" onClick={() => window.open(filePreview.rawUrl, "_blank")} data-testid="approval-preview-download">
                  <Download size={13} /> İndir
                </Button>
              </div>
              <div className="flex-1 overflow-auto bg-slate-100">
                {filePreview.kind === "image" ? (
                  <div className="min-h-full flex items-start justify-center p-4">
                    <img src={filePreview.url} alt={filePreview.file.original_filename} className="max-w-full" data-testid="approval-preview-image" />
                  </div>
                ) : (
                  <iframe title="dosya-onizleme" src={filePreview.url} className="w-full h-full bg-white" data-testid="approval-preview-frame" allowFullScreen />
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-md" data-testid="approve-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight">Teklifi Onayla</DialogTitle>
          </DialogHeader>
          {confirm && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                <strong>{confirm.company_name}</strong> firmasının <strong>{fmt(confirm.price)} {confirm.currency}</strong> tutarındaki teklifini onaylamak üzeresiniz.
                Onayınızla bu teklif sistemde kabul edilir, diğer teklifler reddedilir ve yönetim ekibi bilgilendirilir.
              </p>
              <div className="space-y-1.5">
                <Label>Not (opsiyonel)</Label>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="örn. Fiyat/performans açısından uygun bulunmuştur..." data-testid="approve-note-input" className="rounded-none border-slate-200" />
              </div>
              <Button onClick={approve} disabled={approving} data-testid="approve-confirm-button" className="w-full rounded-none h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold transition-colors duration-200">
                {approving ? "Onaylanıyor..." : "Onaylıyorum"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
